import React, { useEffect, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { Box, Text, Button, Group, Modal, Textarea, NumberInput, Loader, Center, ActionIcon, Menu, Badge } from '@mantine/core'
import { format, formatDistanceToNow } from 'date-fns'
import { toast } from 'react-toastify'
import { IoEllipsisHorizontal } from 'react-icons/io5'
import agent from '../../app/api/agent'
import { useStore } from '../../app/stores/store'
import { ReloaderRecipeDto } from '../../app/models/reloaderRecipe'
import { AmmoScoreBadge } from '../../app/common/ui'

export default function RecipeDetail () {
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const { userStore } = useStore()
  const [recipe, setRecipe] = useState<ReloaderRecipeDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [validatingForm, setValidatingForm] = useState(false)
  const [actualVelocity, setActualVelocity] = useState<number | undefined>()
  const [firearmUsed, setFirearmUsed] = useState('')
  const [validationNotes, setValidationNotes] = useState('')
  const [flagModalOpen, setFlagModalOpen] = useState(false)
  const [flagReason, setFlagReason] = useState('')

  useEffect(() => {
    const loadRecipe = async () => {
      try {
        const data = await agent.ReloaderRecipes.details(id)
        setRecipe(data)
      } catch (error) {
        toast.error('Recipe not found')
        history.push('/ammo/reloading')
      } finally {
        setLoading(false)
      }
    }
    loadRecipe()
  }, [id, history])

  const handleValidate = async () => {
    if (!recipe || !actualVelocity || !firearmUsed.trim()) {
      toast.error('Please fill in all required fields')
      return
    }
    setValidatingForm(true)
    try {
      await agent.ReloaderRecipes.validate(recipe.id, {
        actualVelocityFps: actualVelocity,
        firearmUsed: firearmUsed.trim(),
        notes: validationNotes.trim() || undefined
      })
      toast.success('Validation added!')
      setActualVelocity(undefined)
      setFirearmUsed('')
      setValidationNotes('')
      // Reload recipe
      const updated = await agent.ReloaderRecipes.details(id)
      setRecipe(updated)
    } catch (error) {
      toast.error('Failed to add validation')
    } finally {
      setValidatingForm(false)
    }
  }

  const handleRemoveValidation = async (validationId: string) => {
    if (!recipe) return
    try {
      await agent.ReloaderRecipes.unvalidate(recipe.id)
      toast.success('Validation removed')
      const updated = await agent.ReloaderRecipes.details(id)
      setRecipe(updated)
    } catch (error) {
      toast.error('Failed to remove validation')
    }
  }

  const handleFlag = async () => {
    if (!recipe || !flagReason.trim()) {
      toast.error('Please provide a reason')
      return
    }
    try {
      await agent.ReloaderRecipes.flag(recipe.id, flagReason)
      toast.success('Recipe flagged')
      setFlagModalOpen(false)
      const updated = await agent.ReloaderRecipes.details(id)
      setRecipe(updated)
    } catch (error) {
      toast.error('Failed to flag recipe')
    }
  }

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    )
  }

  if (!recipe) {
    return <Text>Recipe not found</Text>
  }

  return (
    <Box sx={{ fontFamily: 'DM Sans, sans-serif', maxWidth: 760, margin: '0 auto', padding: 24 }}>
      {/* Back Link */}
      <Button
        variant="subtle"
        onClick={() => history.push('/ammo/reloading')}
        sx={{ paddingLeft: 0, marginBottom: 16 }}
      >
        ← Back to Reloader Forum
      </Button>

      {/* Safety Banner */}
      {recipe.isFlagged && (
        <Box sx={{ background: 'var(--gs-danger)', padding: 16, borderRadius: 6, marginBottom: 16 }}>
          <Text weight={600} sx={{ color: '#fff', marginBottom: 4 }}>
            ⚠️ Safety Warning
          </Text>
          <Text size="sm" sx={{ color: '#fff' }}>
            This recipe has been flagged for safety review. Reason: {recipe.flagReason}. Use extreme caution.
          </Text>
        </Box>
      )}

      {/* Header */}
      <Box mb="lg">
        <Text component={Link} to={`/ammo/specs/${recipe.cartridgeSpecId}`} sx={{ fontFamily: 'Oswald, sans-serif', fontSize: 28, fontWeight: 600, color: 'var(--gs-text)', textDecoration: 'none' }}>
          {recipe.cartridgeSpecName}
        </Text>
      </Box>

      {/* Spec Table */}
      <Box sx={{ border: '1px solid var(--gs-border)', borderRadius: 6, overflow: 'hidden', marginBottom: 24 }}>
        <SpecRow label="Powder" value={`${recipe.powderBrand} ${recipe.powderType}`} />
        <SpecRow label="Charge Weight" value={`${recipe.chargeWeightGrMin}-${recipe.chargeWeightGrMax}gr`} />
        <SpecRow label="Primer" value={`${recipe.primerBrand} ${recipe.primerType}`} />
        {recipe.brassBrand && <SpecRow label="Brass" value={recipe.brassBrand} />}
        <SpecRow label="Bullet" value={`${recipe.bulletWeightGr}gr ${recipe.bulletType} — ${recipe.bulletBrand}`} />
        <SpecRow label="OAL" value={`${recipe.oalIn}"`} />
        {recipe.coalIn && <SpecRow label="COAL" value={`${recipe.coalIn}"`} />}
        {recipe.seatingDepthIn && <SpecRow label="Seating Depth" value={`${recipe.seatingDepthIn}"`} />}
        {recipe.estimatedVelocityFps && <SpecRow label="Est. Velocity" value={`${recipe.estimatedVelocityFps} fps`} />}
        <SpecRow label="Intended Use" value={recipe.intendedUse} />
      </Box>

      {/* Chrono Data */}
      {recipe.chronoData && (
        <Box mb="lg">
          <Text weight={600} size="sm" mb={8}>Chrono Data</Text>
          <Box sx={{ background: 'var(--gs-surface)', padding: 12, borderRadius: 4, fontFamily: 'JetBrains Mono, monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}>
            {recipe.chronoData}
          </Box>
        </Box>
      )}

      {/* Notes */}
      {recipe.notes && (
        <Box mb="lg">
          <Text weight={600} size="sm" mb={8}>Notes</Text>
          <Text size="sm" sx={{ lineHeight: 1.6 }}>{recipe.notes}</Text>
        </Box>
      )}

      {/* Validations Section */}
      <Box mb="lg">
        <Group mb="md">
          <Text weight={700} size="lg">Community Results</Text>
          <Badge color="green" size="lg">{recipe.validationCount} Validated</Badge>
        </Group>

        {/* Validate Form */}
        {userStore.isLoggedIn && !recipe.isValidatedByCurrentUser && (
          <Box sx={{ background: 'var(--gs-surface)', padding: 16, borderRadius: 6, marginBottom: 16 }}>
            <Text weight={600} size="sm" mb="sm">I ran this recipe</Text>
            <NumberInput
              label="Actual Velocity (fps)"
              placeholder="1234"
              value={actualVelocity}
              onChange={setActualVelocity}
              mb="sm"
            />
            <NumberInput
              label="Firearm Used"
              placeholder="e.g., 16-inch AR-15 5.56"
              value={firearmUsed}
              onChange={(v) => setFirearmUsed(String(v || ''))}
              mb="sm"
            />
            <Textarea
              label="Notes (optional)"
              placeholder="Any issues or observations?"
              value={validationNotes}
              onChange={(e) => setValidationNotes(e.currentTarget.value)}
              mb="sm"
            />
            <Button size="sm" onClick={handleValidate} loading={validatingForm}>
              Submit Result
            </Button>
          </Box>
        )}

        {/* Validation Cards */}
        {recipe.validations && recipe.validations.length > 0 && (
          <Box>
            {recipe.validations.map((validation) => (
              <Box
                key={validation.id}
                sx={{
                  padding: 12,
                  border: '1px solid var(--gs-border)',
                  borderRadius: 4,
                  marginBottom: 8
                }}
              >
                <Group position="apart" mb="xs">
                  <Group spacing="xs">
                    <Text component={Link} to={`/profiles/${validation.validatorUsername}`} weight={500} size="sm" sx={{ color: 'var(--gs-accent)' }}>
                      {validation.validatorDisplayName || validation.validatorUsername}
                    </Text>
                    <AmmoScoreBadge tier="Newcomer" score={0} size="xs" />
                  </Group>
                  {recipe.isValidatedByCurrentUser && validation.id === recipe.currentUserValidationId && (
                    <Button size="xs" variant="light" color="red" onClick={() => handleRemoveValidation(validation.id)}>
                      Remove my result
                    </Button>
                  )}
                </Group>
                <Text size="sm" weight={600} sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  Got {validation.actualVelocityFps} fps with {validation.firearmUsed}
                </Text>
                {validation.notes && (
                  <Text size="sm" color="dimmed" mt={4}>{validation.notes}</Text>
                )}
                <Text size="xs" color="dimmed" mt={4}>
                  {formatDistanceToNow(new Date(validation.createdAt), { addSuffix: true })}
                </Text>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Author & Actions */}
      <Box sx={{ padding: 16, background: 'var(--gs-surface)', borderRadius: 6, marginTop: 24 }}>
        <Group position="apart" align="center">
          <Group spacing="xs">
            <Text component={Link} to={`/profiles/${recipe.authorUsername}`} weight={600} sx={{ color: 'var(--gs-accent)' }}>
              {recipe.authorDisplayName || recipe.authorUsername}
            </Text>
            <AmmoScoreBadge tier={recipe.authorAmmoTier} score={recipe.authorAmmoScore} size="sm" />
            <Text size="xs" color="dimmed">
              {format(new Date(recipe.createdAt), 'MMM d, yyyy')}
            </Text>
          </Group>

          {(userStore.user?.isModerator || userStore.user?.isAdmin) && (
            <Menu position="bottom-end">
              <Menu.Target>
                <ActionIcon variant="subtle" size="sm">
                  <IoEllipsisHorizontal size={16} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                {userStore.user?.isModerator && !recipe.isFlagged && (
                  <Menu.Item onClick={() => setFlagModalOpen(true)}>Flag for Review</Menu.Item>
                )}
                {userStore.user?.isAdmin && (
                  <Menu.Item color="red">Hide Recipe</Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>
      </Box>

      {/* Flag Modal */}
      <Modal opened={flagModalOpen} onClose={() => setFlagModalOpen(false)} title="Flag Recipe for Review">
        <Textarea
          label="Reason"
          placeholder="Why is this recipe unsafe?"
          value={flagReason}
          onChange={(e) => setFlagReason(e.currentTarget.value)}
          mb="md"
        />
        <Group position="right">
          <Button variant="light" onClick={() => setFlagModalOpen(false)}>Cancel</Button>
          <Button color="red" onClick={handleFlag}>Flag</Button>
        </Group>
      </Modal>
    </Box>
  )
}

function SpecRow ({ label, value }: { label: string; value: string }) {
  return (
    <Group
      spacing="lg"
      noWrap
      sx={{
        padding: '12px',
        borderBottom: '1px solid var(--gs-border)',
        '&:last-child': { borderBottom: 'none' }
      }}
    >
      <Text weight={600} sx={{ background: 'var(--gs-surface)', padding: '12px', minWidth: 140 }}>
        {label}
      </Text>
      <Text sx={{ fontFamily: 'JetBrains Mono, monospace', flex: 1 }}>{value}</Text>
    </Group>
  )
}
